import { Link } from 'react-router';
import { usePathname } from '@/lib/navigation';
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';

interface SidebarRowProps {
    icon: LucideIcon;
    label: string;
    href: string;
    badge?: string | number;
    /** Legacy / equivalent routes that should also light this row as active. */
    aliases?: string[];
    className?: string;
}

export default function SidebarRow({
    icon: Icon,
    label,
    href,
    badge,
    aliases,
    className,
}: SidebarRowProps) {
    const pathname = usePathname();
    const matchesRoute = (route: string) => pathname === route || pathname.startsWith(route + '/');
    const selected = matchesRoute(href) || (aliases ?? []).some(matchesRoute);

    return (
        <Link
            to={href}
            aria-current={selected ? 'page' : undefined}
            className={cn(
                'group relative flex min-h-9 w-full items-center gap-2 px-2.5 py-1.5 text-left',
                'rounded-[8px] outline-none transition-colors duration-card-hover',
                'focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.28)]',
                // Active = soft fill + weight (no left stripe, no persona accent on rows).
                selected
                    ? 'bg-[rgb(var(--color-sidebar-hover)/0.06)]'
                    : 'hover:bg-[rgb(var(--color-sidebar-hover)/0.035)]',
                className,
            )}
        >
            <span
                className={cn(
                    'grid h-7 w-7 flex-shrink-0 place-items-center rounded-[7px] transition-colors',
                    selected
                        ? 'text-[rgb(var(--color-text-primary))]'
                        : 'text-[rgb(var(--color-text-secondary))] group-hover:text-[rgb(var(--color-text-primary))]',
                )}
            >
                <Icon className="h-4 w-4" />
            </span>
            <span
                className={cn(
                    'flex-1 truncate text-[13.5px] leading-5',
                    selected
                        ? 'font-semibold text-[rgb(var(--color-text-primary))]'
                        : 'font-medium text-[rgb(var(--color-text-secondary))] group-hover:text-[rgb(var(--color-text-primary))]',
                )}
            >
                {label}
            </span>
            {badge !== undefined && (
                <span
                    className={cn(
                        'min-w-5 rounded-full px-1.5 py-0.5 text-center text-[10.5px] leading-4 font-medium',
                        selected
                            ? 'bg-[rgb(var(--color-text-primary)/0.08)] text-[rgb(var(--color-text-primary))]'
                            : 'bg-[rgb(var(--color-text-primary)/0.06)] text-[rgb(var(--color-text-tertiary))]',
                    )}
                >
                    {badge}
                </span>
            )}
        </Link>
    );
}
